import { useEffect, useRef, useState, useCallback } from 'react';

export interface IdleTimeoutConfig {
  /** Seconds of inactivity before warning dialog appears (default: 360 = 6 min) */
  idleSeconds: number;
  /** Seconds of countdown in the warning dialog before auto-logout (default: 180 = 3 min) */
  timeoutSeconds: number;
  /** Whether idle detection is active */
  enabled: boolean;
}

export interface IdleTimeoutState {
  /** Warning dialog is visible */
  showWarning: boolean;
  /** Seconds remaining in the countdown */
  secondsLeft: number;
}

const USER_ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'click',
];

/**
 * useIdleTimeout — mirrors Angular's `angular-user-idle` behavior.
 *
 * 1. After `idleSeconds` of no user activity → shows warning dialog (countdown).
 * 2. If user interacts during countdown → resets (dialog closes).
 * 3. If countdown reaches 0 → calls `onTimeout()`.
 */
export function useIdleTimeout(
  config: IdleTimeoutConfig,
  onTimeout: () => void,
): IdleTimeoutState & { resetIdle: () => void; stayLoggedIn: () => void } {
  const { idleSeconds, timeoutSeconds, enabled } = config;

  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(timeoutSeconds);

  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isWarningRef = useRef(false);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const clearCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const startCountdown = useCallback(() => {
    isWarningRef.current = true;
    setShowWarning(true);
    setSecondsLeft(timeoutSeconds);

    let remaining = timeoutSeconds;
    countdownRef.current = setInterval(() => {
      remaining -= 1;
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        clearInterval(countdownRef.current!);
        countdownRef.current = null;
        isWarningRef.current = false;
        setShowWarning(false);
        onTimeoutRef.current();
      }
    }, 1000);
  }, [timeoutSeconds]);

  const startIdleTimer = useCallback(() => {
    clearIdleTimer();
    idleTimerRef.current = setTimeout(() => {
      startCountdown();
    }, idleSeconds * 1000);
  }, [idleSeconds, clearIdleTimer, startCountdown]);

  const resetIdle = useCallback(() => {
    clearIdleTimer();
    clearCountdown();
    isWarningRef.current = false;
    setShowWarning(false);
    setSecondsLeft(timeoutSeconds);
    startIdleTimer();
  }, [clearIdleTimer, clearCountdown, timeoutSeconds, startIdleTimer]);

  const stayLoggedIn = useCallback(() => {
    resetIdle();
  }, [resetIdle]);

  // Activity listener — only resets if warning is NOT showing
  useEffect(() => {
    if (!enabled) return;

    const handleActivity = () => {
      if (!isWarningRef.current) {
        // User is active before warning — reset idle timer
        clearIdleTimer();
        startIdleTimer();
      }
    };

    USER_ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Start initial idle timer
    startIdleTimer();

    return () => {
      USER_ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      clearIdleTimer();
      clearCountdown();
    };
  }, [enabled, clearIdleTimer, clearCountdown, startIdleTimer]);

  return { showWarning, secondsLeft, resetIdle, stayLoggedIn };
}
